import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import apiClient from '../../services/apiClient';

const inviteApi = {
  get:    (token) => apiClient.get(`/api/teams/invite/${token}`).then(r => r.data),
  accept: (data)  => apiClient.post('/api/teams/invite/accept', data).then(r => r.data),
};

export default function AcceptInvite() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [done,     setDone]     = useState(false);

  // Fetch invite info
  const { data, isLoading, error } = useQuery({
    queryKey: ['invite', token],
    queryFn:  () => inviteApi.get(token),
    enabled:  !!token,
    retry: false,
  });
  const invite = data?.data;

  const acceptMut = useMutation({
    mutationFn: () => inviteApi.accept({ token, password }),
    onSuccess: () => { setDone(true); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to accept invite'),
  });

  const handleAccept = (e) => {
    e.preventDefault();
    if (password.length < 8)         { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm)         { toast.error('Passwords do not match'); return; }
    acceptMut.mutate();
  };

  if (!token) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Card className="p-8 text-center max-w-md w-full">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <div className="text-base font-semibold text-slate-900">Invalid invite link</div>
        <div className="text-xs text-slate-500 mt-1">No token found in the URL.</div>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* SlipSense logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#2E6DA4] flex items-center justify-center">
              <span className="text-white font-bold text-sm">S</span>
            </div>
            <span className="text-xl font-bold text-slate-900">SlipSense</span>
          </div>
        </div>

        <Card className="p-6 sm:p-8 border-slate-200 shadow-sm">
          {isLoading ? (
            <div className="text-center py-8 text-slate-400">Loading invite…</div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <div className="text-base font-semibold text-slate-900 mb-1">Invite unavailable</div>
              <div className="text-xs text-slate-500">
                {error.response?.data?.message || 'This invite is invalid or has expired.'}
              </div>
              <Button onClick={() => navigate('/login')} variant="outline" className="mt-4">Go to Login</Button>
            </div>
          ) : done ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <div className="text-base font-semibold text-slate-900 mb-1">You're in! 🎉</div>
              <div className="text-xs text-slate-500 mb-4">
                Your account has been set up. Log in with <span className="font-medium">{invite?.email}</span> and the password you just created.
              </div>
              <Button onClick={() => navigate('/login')} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white w-full">
                Log In Now
              </Button>
            </div>
          ) : (
            <>
              {/* Invite info */}
              <div className="mb-6">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">You've been invited to join</div>
                <div className="text-2xl font-bold text-slate-900 font-heading">{invite?.businessName}</div>
                <div className="text-sm text-slate-500 mt-1">
                  Invited by <span className="font-medium text-slate-700">{invite?.ownerName}</span>
                  {' · '}Role: <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">{invite?.role}</span>
                </div>
              </div>

              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 mb-6">
                <div className="text-xs text-slate-500">Your email address</div>
                <div className="text-sm font-medium text-slate-900">{invite?.email}</div>
              </div>

              <form onSubmit={handleAccept} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Set Your Password</Label>
                  <div className="relative">
                    <Input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      className="pr-10"
                    />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Confirm Password</Label>
                  <Input
                    type="password" value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                  />
                </div>

                <Button type="submit" disabled={acceptMut.isPending}
                  className="bg-[#2E6DA4] hover:bg-[#255a88] text-white w-full h-11 text-sm font-semibold">
                  {acceptMut.isPending ? 'Setting up account…' : 'Accept Invite & Set Password'}
                </Button>
              </form>

              <p className="text-[10px] text-slate-400 text-center mt-4">
                Invite expires {invite?.expiresAt ? new Date(invite.expiresAt).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : ''}
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}